import { PanelRightClose, PanelRightOpen } from 'lucide-react'
import * as React from 'react'

import { LocalStorageKeys, useLocalStorage } from '@/lib/storage/local-storage.hook'
import { cn } from '@/lib/ui/style-helpers'

import { Button } from '@/components/lemmala-design-system/button/button'

import { Sidebar } from './sidebar'
import type { SidebarEntry, SidebarNavItemFields } from './sidebar.types'

export interface CollapsibleSidebarProps {
  logo: React.ReactNode
  /** Compact logo for the collapsed rail (e.g. icon only). */
  compactLogo: React.ReactNode
  entries: SidebarEntry[]
  bottomItems: SidebarNavItemFields[]
  /** User profile / bottom block. Receives expanded state to show e.g. name next to avatar when expanded. */
  userBlock: (options: { expanded: boolean }) => React.ReactNode
  /** Optional tenant switcher. Receives expanded state so the component can adapt its layout. */
  tenantSwitcher?: (options: { expanded: boolean }) => React.ReactNode
  className?: string
}

interface SidebarContentProps {
  expanded: boolean
  logo: React.ReactNode
  compactLogo: React.ReactNode
  entries: SidebarEntry[]
  bottomItems: SidebarNavItemFields[]
  headerAction?: React.ReactNode
  tenantSwitcher?: (options: { expanded: boolean }) => React.ReactNode
  userBlock: (options: { expanded: boolean }) => React.ReactNode
}

const SidebarContent = ({
  expanded,
  logo,
  compactLogo,
  entries,
  bottomItems,
  headerAction,
  tenantSwitcher,
  userBlock,
}: SidebarContentProps) => (
  <>
    <Sidebar
      logo={expanded ? logo : compactLogo}
      entries={entries}
      bottomItems={bottomItems}
      headerAction={expanded ? headerAction : undefined}
      tenantSwitcher={tenantSwitcher?.({ expanded }) ?? undefined}
      collapsed={!expanded}
      className='min-h-0 flex-1 border-r-0 bg-transparent shadow-none'
    />
    <div className={cn('shrink-0 pb-4', expanded ? 'px-4' : 'px-2')}>{userBlock({ expanded })}</div>
  </>
)

export const CollapsibleSidebar = React.forwardRef<HTMLDivElement, CollapsibleSidebarProps>(
  ({ logo, compactLogo, entries, bottomItems, userBlock, tenantSwitcher, className }, ref) => {
    const [storedPinned, setStoredPinned] = useLocalStorage(LocalStorageKeys.sidebarPinned)

    const isPinnedExpanded = storedPinned ?? true
    const [suppressHover, setSuppressHover] = React.useState(false)

    const headerAction = React.useMemo(
      () => (
        <Button
          variant='ghost'
          size='icon-sm'
          onClick={() => {
            if (isPinnedExpanded) setSuppressHover(true)
            setStoredPinned(!isPinnedExpanded)
          }}
          aria-label={isPinnedExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-expanded={isPinnedExpanded}
          aria-controls='sidebar-nav'
          data-testid='sidebar-toggle'
          className='shrink-0'
        >
          {isPinnedExpanded ? (
            <PanelRightClose className='size-4' aria-hidden />
          ) : (
            <PanelRightOpen className='size-4' aria-hidden />
          )}
        </Button>
      ),
      [isPinnedExpanded, setStoredPinned],
    )

    return (
      <div
        ref={ref}
        className={cn('relative z-50 flex h-full flex-col', className)}
        data-testid='collapsible-sidebar'
      >
        {isPinnedExpanded ? (
          <div
            className='flex h-full w-45 shrink-0 flex-col border-r border-main-border-default bg-main-background-card'
            data-testid='sidebar-expanded-panel'
          >
            <SidebarContent
              expanded={true}
              logo={logo}
              compactLogo={compactLogo}
              entries={entries}
              bottomItems={bottomItems}
              headerAction={headerAction}
              tenantSwitcher={tenantSwitcher}
              userBlock={userBlock}
            />
          </div>
        ) : (
          <div className='group relative h-full' onMouseLeave={() => setSuppressHover(false)}>
            <div
              className='flex h-full w-16 shrink-0 flex-col border-r border-main-border-default bg-main-background-card'
              data-testid='sidebar-rail'
            >
              <SidebarContent
                expanded={false}
                logo={logo}
                compactLogo={compactLogo}
                entries={entries}
                bottomItems={bottomItems}
                headerAction={headerAction}
                tenantSwitcher={tenantSwitcher}
                userBlock={userBlock}
              />
            </div>
            <div
              className={cn(
                'absolute bottom-0 left-0 top-0 z-50 flex w-45 flex-col border-r border-main-border-default bg-main-background-card shadow-md',
                suppressHover ? 'invisible' : 'invisible group-hover:visible',
              )}
              data-testid='sidebar-overlay'
            >
              <SidebarContent
                expanded={true}
                logo={logo}
                compactLogo={compactLogo}
                entries={entries}
                bottomItems={bottomItems}
                headerAction={headerAction}
                tenantSwitcher={tenantSwitcher}
                userBlock={userBlock}
              />
            </div>
          </div>
        )}
      </div>
    )
  },
)
CollapsibleSidebar.displayName = 'CollapsibleSidebar'
